
import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { DataCompressionService } from "./data-compression.service";
import { QrCodeService } from "./qr-code.service";
import { MensagemService } from "./mensagem.service";
import { ErroImportacaoService } from "./erro-importacao.service";

@Injectable({
    providedIn: 'root'
})
export class DadosCalculadoraService {

    input: any = null;
    output: any = null;
    extra: any = null;
    hash: any = null;

    xml: any = null;

    versaoExecucao: any = null;
    versaoImportacao: any = null;

    header: any = null;

    private dadosSubject = new Subject<any>();

    dados$ = this.dadosSubject.asObservable();

    constructor(
        private dataCompressionService: DataCompressionService,
        private qrCodeService: QrCodeService,
        private mensagemService: MensagemService,
        private erroImportacaoService: ErroImportacaoService
    ) { }

    limparDados() {
        this.input = null;
        this.output = null;
        this.extra = null;
        this.xml = null;
        this.versaoImportacao = null;
        this.header = null;
    }

    importarDados(dados: any) {
        try {
            //const dadosString = atob(dados);
            const dadosObject = JSON.parse(dados);
            this.input = dadosObject.input;
            this.output = dadosObject.output;
            this.extra = dadosObject.extra;
            //this.hash = dadosObject.hash;
            //this.dadosSubject.next({ input: this.input, output: this.output, extra: this.extra });
            // Verificar se o arquivo tem hash para validação
            if (!dadosObject.hash) {
                console.warn('AVISO - Arquivo não possui hash para validação');
                return; // Continua sem validação
            }
            const dadosSemHash = {
                input: this.input,
                output: this.output,
                extra: this.extra
            };
            // Garantir ordem consistente das propriedades no JSON
            // Reorganizar objeto para garantir ordem consistente das chaves principais
            const dadosOrdenados = {
                extra: dadosSemHash.extra,
                input: dadosSemHash.input,
                output: dadosSemHash.output
            };
            const jsonString = JSON.stringify(dadosOrdenados);
            const hashCalculado = this.erroImportacaoService.gerarHash(jsonString);
            if (hashCalculado !== dadosObject.hash) {
                this.erroImportacaoService.temErro = true;
                console.error('Erro de validação do hash.');
                this.mensagemService.mostrar('Erro', 'Erro', 'Erro ao importar dados do arquivo. Verifique se o arquivo não foi modificado indevidamente.');
                return;
            }
        } catch (e) {
            console.error('Erro ao importar dados:', e);
            this.mensagemService.mostrar('Erro', 'Erro', 'Erro ao importar dados do arquivo. Verifique se o arquivo é válido.');
        }
    }

    exportarDados() {
        // Verificar se há dados para exportar
        if (!this.input && !this.output && !this.extra) {
            console.warn('AVISO - Não há dados para exportar.');
            this.mensagemService.mostrar('Aviso', 'Aviso', 'Não há dados para exportar. Execute uma simulação primeiro.');
            return;
        }
        const dados = {
            input: this.input,
            output: this.output,
            extra: this.extra
        }
        // Garantir ordem consistente das propriedades para gerar hash
        // Reorganizar objeto para garantir ordem consistente das chaves principais
        const dadosOrdenados = {
            extra: dados.extra,
            input: dados.input,
            output: dados.output
        };
        const jsonString = JSON.stringify(dadosOrdenados);
        const hash = this.erroImportacaoService.gerarHash(jsonString);
        const dadosComHash = {
            input: this.input,
            output: this.output,
            extra: this.extra,
            hash: hash
        }
        const jsonStringFinal = JSON.stringify(dadosComHash, null, 2);
        const blob = new Blob([jsonStringFinal], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dados-calculadora-${new Date().toISOString().slice(0, 10)}.json`;
        a.click();
        URL.revokeObjectURL(url);
    }



    /**
     * Gerar uma URL compartilhável com dados comprimidos
     */
    gerarUrlCompartilhamento(): string {
        const dados = {
            input: this.input,
            output: this.output,
            extra: this.extra
        };

        return this.dataCompressionService.generateShareableUrl(dados, window.location.href);
    }

    async copiarUrlParaClipboard(): Promise<boolean> {
        try {
            const url = this.gerarUrlCompartilhamento();
            await navigator.clipboard.writeText(url);
            return true;
        } catch (error) {
            console.error('Erro ao copiar URL:', error);
            return false;
        }
    }

    importarDadosDeUrl(): boolean {
        try {
            const dados = this.dataCompressionService.extractDataFromUrl();
            if (dados) {
                this.input = dados.input;
                this.output = dados.output;
                this.extra = dados.extra;
                return true;
            }
            return false;
        } catch (error) {
            console.error('Erro ao importar dados da URL:', error);
            return false;
        }
    }

    obterEstatisticasCompressao(): { original: number; compressed: number; ratio: string } | null {
        if (!this.input && !this.output && !this.extra) {
            return null;
        }

        const dados = {
            input: this.input,
            output: this.output,
            extra: this.extra
        };

        return this.dataCompressionService.getCompressionStats(dados);
    }

    async gerarQrCode(): Promise<string> {
        const url = this.gerarUrlCompartilhamento();
        return await this.qrCodeService.generateQrCode(url);
    }

    async baixarQrCode(): Promise<Blob> {
        try {
            const url = this.gerarUrlCompartilhamento();
            // Validar comprimento da URL antes de gerar o QR Code
            if (url.length > 4296) {
                throw new Error(`URL muito longa para QR Code: ${url.length} caracteres (máximo: 4296)`);
            }

            const filename = `calculadora-qr-${new Date().toISOString().slice(0, 10)}.png`;
            return await this.qrCodeService.downloadQrCode(url, filename);
        } catch (error) {
            console.error('Erro detalhado ao gerar QR Code:', error);
            throw error;
        }
    }

    validarTamanhoParaQrCode(): { isValid: boolean; maxLength: number; actualLength: number } {
        const url = this.gerarUrlCompartilhamento();
        return this.qrCodeService.validateTextLength(url);
    }

}
